import { useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { Seo } from '@/components/seo/Seo';
import { useLogout } from '@/api/auth';
import { useAdminDashboard } from '@/api/admin';
import { useAuthStore } from '@/stores/auth-store';
import { toast } from '@/components/ui';
import { cn } from '@/lib/cn';
import { LanguageSwitcher } from '@/components/layout/LanguageSwitcher';
import { t } from '@/i18n';

interface AdminNavItem {
  to: string;
  label: string;
  /** Bekleyen iş sayısı gösterilecek bölüm. */
  badge?: 'companies' | 'listings' | 'reviews';
}

/*
 * Etiketler ÇAĞRI ANINDA üretiliyor.
 *
 * Modül seviyesinde `t()` çağıran bir sabit, import edildiği an bir kez hesaplanıp
 * donuyordu: dil değiştirilince bu metinler eski dilde kalıyordu. Rotalar `React.lazy`
 * ile bölündüğü için sonuç tutarsız da oluyordu — dil değişiminden önce yüklenmiş
 * sayfa eski, sonra yüklenen yeni dilde geliyordu.
 */
function navItems(): AdminNavItem[] {
  return [
    { to: '/admin', label: t('admin.navDashboard') },
    { to: '/admin/firmalar', label: t('admin.navCompanies'), badge: 'companies' },
    { to: '/admin/ilanlar', label: t('admin.navListings'), badge: 'listings' },
    { to: '/admin/kullanicilar', label: t('admin.navUsers') },
    { to: '/admin/siparisler', label: t('admin.navOrders') },
    { to: '/admin/numuneler', label: t('admin.navSamples') },
    { to: '/admin/fiyat-talepleri', label: t('admin.navPriceRequests') },
    { to: '/admin/degerlendirmeler', label: t('admin.navReviews'), badge: 'reviews' },
    { to: '/admin/icerik', label: t('admin.navContent') },
    { to: '/admin/kategoriler', label: t('admin.navCategories') },
    { to: '/admin/ayarlar', label: t('admin.navSettings') },
    { to: '/admin/denetim', label: t('admin.navAudit') },
  ];
}

/** Yönetim çerçevesi: koyu sidebar + bekleyen iş rozetleri (docs/05 §5). */
export function AdminLayout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const user = useAuthStore((state) => state.user);
  const dashboard = useAdminDashboard();
  const logout = useLogout();
  const navigate = useNavigate();

  async function handleLogout() {
    await logout.mutateAsync().catch(() => undefined);
    toast.success(t('auth.logoutSuccess'));
    navigate('/', { replace: true });
  }

  return (
    <div className="flex min-h-screen bg-canvas">
      <Seo title={t('admin.metaTitle')} noIndex />

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 w-60 shrink-0 bg-slate-900 text-slate-200 lg:static lg:block',
          menuOpen ? 'block' : 'hidden',
        )}
      >
        <div className="flex h-16 items-center gap-2 border-b border-slate-800 px-4">
          <span
            aria-hidden="true"
            className="grid h-8 w-8 place-items-center rounded-lg bg-primary-800 text-sm font-bold text-white"
          >
            B
          </span>
          <span className="font-semibold text-white">{t('admin.title')}</span>
        </div>

        <nav className="space-y-1 p-3" aria-label={t('admin.title')}>
          {navItems().map((item) => {
            const pending = item.badge ? (dashboard.data?.pending[item.badge] ?? 0) : 0;

            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/admin'}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  cn(
                    'flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm transition',
                    isActive
                      ? 'bg-primary-800 font-medium text-white'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white',
                  )
                }
              >
                {item.label}
                {pending > 0 && (
                  <span className="grid h-5 min-w-5 place-items-center rounded-full bg-amber-400 px-1 text-[11px] font-semibold text-slate-900">
                    {pending}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="mt-auto space-y-1 p-3 text-sm">
          <Link
            to="/"
            className="block rounded-lg px-3 py-2 text-slate-400 transition hover:bg-slate-800 hover:text-white"
          >
            ← {t('admin.backToSite')}
          </Link>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="border-b border-slate-200 bg-surface">
          <div className="flex h-16 items-center justify-between gap-4 px-4 sm:px-6">
            <button
              type="button"
              onClick={() => setMenuOpen((open) => !open)}
              aria-expanded={menuOpen}
              aria-label={t('admin.title')}
              className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 lg:hidden"
            >
              <span aria-hidden="true" className="block text-lg leading-none">
                ☰
              </span>
            </button>

            <div className="ml-auto flex items-center gap-3">
              <span className="hidden text-sm text-slate-600 sm:inline">{user?.email}</span>
              <LanguageSwitcher />
              <button
                type="button"
                onClick={handleLogout}
                className="rounded-lg px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
              >
                {t('nav.logout')}
              </button>
            </div>
          </div>
        </header>

        <main className="min-w-0 flex-1 p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
